import type { Bid, Card, Suit } from "./protocol";

export type Locale = "en" | "es";

type Params = Record<string, string | number>;

const SUIT_LABELS: Record<Suit, Record<Locale, string>> = {
  oros: { en: "Oros", es: "Oros" },
  copas: { en: "Copas", es: "Copas" },
  espadas: { en: "Espadas", es: "Espadas" },
  bastos: { en: "Bastos", es: "Bastos" },
};

const BID_LABELS: Record<Bid, Record<Locale, string>> = {
  pass: { en: "Pass", es: "Paso" },
  entrada: { en: "Entrada", es: "Entrada" },
  oros: { en: "Entrada Oros", es: "Entrada Oros" },
  volteo: { en: "Volteo", es: "Volteo" },
  solo: { en: "Solo", es: "Solo" },
  solo_oros: { en: "Solo Oros", es: "Solo Oros" },
  bola: { en: "Bola", es: "Bola" },
  contrabola: { en: "Contrabola", es: "Contrabola" },
};

const CONTRACT_LABELS: Record<string, Record<Locale, string>> = {
  entrada: { en: "Entrada", es: "Entrada" },
  volteo: { en: "Volteo", es: "Volteo" },
  solo: { en: "Solo", es: "Solo" },
  oros: { en: "Oros", es: "Oros" },
  solo_oros: { en: "Solo Oros", es: "Solo Oros" },
  contrabola: { en: "Contrabola", es: "Contrabola" },
  bola: { en: "Bola", es: "Bola" },
  penetro: { en: "Penetro", es: "Penetro" },
};

const PHASE_LABELS: Record<string, Record<Locale, string>> = {
  dealing: { en: "Dealing", es: "Reparto" },
  auction: { en: "Auction", es: "Subasta" },
  penetro_choice: { en: "Penetro Choice", es: "Decisión de Penetro" },
  trump_choice: { en: "Choose Trump", es: "Elegir triunfo" },
  exchange: { en: "Exchange", es: "Cambio" },
  play: { en: "Play Trick", es: "Jugar baza" },
  post_hand: { en: "Hand Complete", es: "Mano terminada" },
  scoring: { en: "Scoring", es: "Puntuación" },
  match_end: { en: "Match End", es: "Fin de partida" },
  lobby: { en: "Lobby", es: "Sala" },
  contract_upgrade: { en: "Contract Upgrade", es: "Mejora de contrato" },
};

const POSITION_LABELS: Record<"self" | "left" | "across" | "right", Record<Locale, string>> = {
  self: { en: "You", es: "Tú" },
  left: { en: "Left", es: "Izquierda" },
  across: { en: "Across", es: "Enfrente" },
  right: { en: "Right", es: "Derecha" },
};

const RANK_LABELS: Record<number, Record<Locale, string>> = {
  1: { en: "Ace", es: "As" },
  10: { en: "Jack", es: "Sota" },
  11: { en: "Knight", es: "Caballo" },
  12: { en: "King", es: "Rey" },
};

const MESSAGES: Record<Locale, Record<string, string>> = {
  en: {
    "common.english": "English",
    "common.spanish": "Español",
    "common.language": "Language",
    "common.back": "Back",
    "common.leave": "Leave",
    "common.cancel": "Cancel",
    "common.create": "Create",
    "common.join": "Join",
    "common.save": "Save",
    "common.copy": "Copy",
    "common.copyCode": "Copy Code",
    "common.refresh": "Refresh",
    "common.settings": "Settings",
    "common.profile": "Profile",
    "common.you": "You",
    "common.host": "Host",
    "common.bot": "Bot",
    "common.open": "Open",
    "common.ready": "Ready",
    "common.offline": "Offline",
    "common.mode": "Mode",
    "common.target": "Target",
    "common.deck": "Deck",
    "common.room": "Room",
    "common.winner": "Winner",
    "common.update": "Update",
    "common.loading": "Loading",
    "common.startGame": "Start Game",
    "common.playAgain": "Play Again",
    "common.closeHand": "Close Hand",
    "common.closeModal": "Close modal",
    "player.unknown": "Player",
    "home.heroTag": "The Salon is Waiting",
    "home.welcome": "Welcome, {name}",
    "home.heroSubtitle": "Select your table and claim your glory.",
    "home.selectVariation": "Select Game Variation",
    "home.createRoom": "Create Room",
    "home.createRoomSubtitle": "The Ombre's Salon",
    "home.quickPlay": "Quick Play",
    "home.quickPlaySubtitle": "Instant Match",
    "home.joinByCode": "Join by Code",
    "home.joinByCodeSubtitle": "Private Invitation",
    "home.queueSearching": "Searching for match...",
    "home.queueSearchingPosition": "Searching for match (position {position})",
    "home.connectHint": "Connect to the server to start a match.",
    "home.connected": "Connected to the salon server",
    "home.disconnected": "Disconnected from the salon server",
    "home.accountTitle": "Account",
    "home.accountGuest": "Guest mode",
    "home.signInGoogle": "Continue with Google",
    "home.signOut": "Sign Out",
    "home.signedOut": "Signed out",
    "home.authUnavailable": "Account sign-in is unavailable right now.",
    "home.playStyle": "Play Style",
    "home.playStyleFree": "Free Play",
    "home.playStyleStakes": "Friendly Stakes",
    "home.stakesHint": "Staked tables require a signed-in account and use friendly tokens.",
    "home.walletBalance": "Token Balance",
    "home.walletLoading": "Loading wallet...",
    "home.walletRescue": "Claim Rescue",
    "home.walletRescueCooldown": "Rescue on cooldown",
    "home.walletRefreshFailed": "Unable to refresh wallet right now.",
    "home.walletRescueSuccess": "Rescue tokens claimed.",
    "home.activityTitle": "Recent Table Activity",
    "home.activityLoading": "Loading activity...",
    "home.activityEmpty": "Recent salon results will appear here.",
    "home.activityPot": "Pot {amount}",
    "home.quote": "\"The game of Ombre is a game of wit, fortune, and Spanish pride.\"",
    "home.roomNameOptional": "Room Name (optional)",
    "home.roomNamePlaceholder": "e.g. La Mesa de Oro",
    "home.createRoomTitle": "Create Room",
    "home.joinByCodeTitle": "Join by Code",
    "home.roomCode": "Room Code",
    "home.pointsToWin": "Points to win",
    "home.validRoomCode": "Enter a valid room code",
    "home.chooseNameAvatar": "Choose Name & Avatar",
    "app.connectionLost": "Connection lost - reconnecting...",
    "lobby.sessionActive": "Session Active",
    "lobby.reconnecting": "Reconnecting",
    "lobby.joiningSalon": "Joining your salon",
    "lobby.roomPreparing": "Room {code} is being prepared for the first snapshot.",
    "lobby.preparing": "Preparing Lobby",
    "lobby.fetching": "Fetching seats and room state",
    "lobby.waitSnapshot": "You'll stay here for a moment while the table snapshot arrives from the server.",
    "lobby.privateRoomCode": "Private Room Code",
    "lobby.hostControls": "Host Controls",
    "lobby.matchStatus": "Match Status",
    "lobby.awaitingPlayers": "Awaiting players",
    "lobby.waitingForHost": "Waiting for host to start",
    "lobby.hostSeat": "Host Seat",
    "lobby.yourSeat": "Your Seat",
    "lobby.sitHere": "Sit Here",
    "lobby.matchConfiguration": "Match Configuration",
    "lobby.invitation": "Invitation",
    "lobby.seats": "Seats",
    "lobby.format": "Format",
    "lobby.seatedReady": "You are seated and ready for the first hand.",
    "lobby.botSeatMeta": "This chair will be played by a bot.",
    "lobby.reconnectSeatMeta": "Connection lost. The seat is being held for reconnection.",
    "lobby.claimedReady": "Claimed and ready for the salon.",
    "lobby.openSeatMeta": "Invite a friend or start with a bot.",
    "lobby.openSeat": "Open Seat {seat}",
    "lobby.seat": "Seat {seat}",
    "lobby.joinedToast": "{name} joined",
    "lobby.codeCopied": "Code copied!",
    "lobby.copyFailed": "Failed to copy",
    "lobby.allActiveSeatsClaimed": "All active seats are claimed and ready for the opening hand.",
    "lobby.startNowBots": "Start now and {count} open {seatWord} will be filled with bots.",
    "lobby.emptySeatsBots": "{count} empty {seatWord} will be filled with bots",
    "lobby.shareCode": "Share room code {code} to bring friends straight into this salon.",
    "lobby.activeSeatsClaimed": "Every active seat is claimed and ready for the first deal.",
    "lobby.activeSeatsOpen": "{count} {seatWord} open and can be bot-filled when the game starts.",
    "lobby.withDeck": "{mode} to {target} points with the Spanish 40-card deck.",
    "game.waiting": "Waiting...",
    "game.roundShort": "R: {hand}/{target}",
    "game.roundShortNoTarget": "R: {hand}",
    "game.round": "Round {hand}/{target}",
    "game.phaseAuction": "Phase: Auction",
    "game.auction": "Auction",
    "game.target": "Target: {target}",
    "game.trump": "Trump: {suit}",
    "game.trumpUndecided": "Trump: Undecided",
    "game.turn": "Turn: {name}{suffix}",
    "game.playTrick": "Play Trick",
    "game.phaseChooseTrump": "Phase: Choose Trump",
    "game.chooseTrump": "Choose Trump",
    "game.phaseExchange": "Phase: Exchange",
    "game.exchange": "Exchange",
    "game.exchanging": "Exchanging: {name}",
    "game.talonShort": "Talon: {count}",
    "game.talonRemaining": "Talon: {count} remaining",
    "game.penetro": "Penetro",
    "game.phasePenetro": "Phase: Penetro Choice",
    "game.deciding": "{name} deciding...",
    "game.dealing": "Dealing...",
    "game.handComplete": "Hand Complete",
    "game.matchComplete": "Match Complete",
    "game.ping": "Ping {value}",
    "game.profileMeta": "Room {code}",
    "game.profileMetaFallback": "Table Player",
    "game.backHome": "Back",
    "game.backHomeAria": "Back to home",
    "game.settingsAria": "Settings",
    "game.soundAria": "Toggle sound",
    "game.youUpper": "YOU",
    "game.winner": "WINNER",
    "game.yourHand": "Your Hand",
    "game.yourHandSwipe": "Your Hand (Swipe)",
    "game.yourHandArea": "Your hand area",
    "game.swipeHintMobile": "Swipe",
    "game.tapToConfirm": "Tap again to play",
    "game.yourHandAria": "Your hand",
    "game.swipe": "Swipe",
    "game.selectCard": "Select a Card",
    "game.playCard": "Play Card",
    "game.selectCards": "Select Cards",
    "game.keepAll": "Keep All",
    "game.yourTurn": "YOUR TURN",
    "game.tricksWon": "Tricks won: {count}",
    "game.tricksShort": "{count} tricks",
    "game.ombreRole": "Ombre",
    "game.opponents": "Opponents",
    "game.passed": "Passed",
    "game.resting": "Resting",
    "game.role.you": "You",
    "game.role.ombre": "Player",
    "game.role.firstOpponent": "First opponent",
    "game.role.secondOpponent": "Second opponent",
    "game.role.seat": "Seat {n}",
    "game.announce.pass": "{actor} passes",
    "game.announce.bid": "{actor} bids {bid}",
    "game.announce.auctionWin": "{actor} wins auction with {bid}",
    "game.announce.winWith": "{actor} wins with {bid}",
    "game.announce.cardPlayed": "{actor} played {card}",
    "game.announce.point": "point",
    "game.announce.points": "points",
    "game.announce.roundResultSelf": "Round result: +{points} {pointWord}",
    "game.announce.roundResultOthers": "Round result: {names} +{points}",
    "game.announce.roundComplete": "Round complete",
    "game.announce.trumpSet": "Trump set to {suit}",
    "game.announce.volteoReveal": "Volteo reveals {card}",
    "game.invalid.exactOne": "Select exactly 1 card to exchange.",
    "game.invalid.validExchange": "Select a valid exchange.",
    "game.invalid.waitExchange": "Wait for your exchange turn.",
    "game.invalid.waitTurn": "Wait for your turn.",
    "game.invalid.followSuit": "Illegal card: follow suit if possible.",
    "game.phaseBanner.auctionLeadNone": "No leading bid yet",
    "game.phaseBanner.yourTurnChooseBid": "Your turn: choose a bid",
    "game.phaseBanner.leadingBid": "Leading bid: {bid} by {name}",
    "game.phaseBanner.yourTurnChooseBidWithLeader": "Your turn: choose a bid · {leader}",
    "game.phaseBanner.yourTurnChooseTrump": "Your turn: choose trump",
    "game.phaseBanner.waitChooseTrump": "Waiting for {name} to choose trump",
    "game.phaseBanner.waitTrump": "Waiting for trump selection",
    "game.phaseBanner.selectExchange": "Select cards to exchange",
    "game.phaseBanner.volteoDiscard": "Choose at least 1 card: first discard returns you to 9",
    "game.phaseBanner.waitExchange": "Waiting for {name} to exchange",
    "game.phaseBanner.preparingExchange": "Preparing exchange",
    "game.phaseBanner.penetroDecision": "Resting player decides Penetro",
    "game.phaseBanner.waitLead": "Waiting for lead",
    "game.phaseBanner.yourTurnPlay": "Your turn: play a legal card",
    "game.phaseBanner.waitPlay": "Waiting for {name} to play",
    "game.phaseBanner.contractUpgradeOwn": "You won the auction! Upgrade your contract?",
    "game.phaseBanner.contractUpgradeWait": "Declarer is considering an upgrade…",
    "game.phaseBanner.dealing": "Dealing cards",
    "game.phaseBanner.handComplete": "Hand complete",
    "game.phaseBanner.matchComplete": "Match complete",
    "game.kicker.trickResult": "Trick Result",
    "game.kicker.yourTurn": "Your Turn",
    "game.kicker.update": "Update",
    "game.loading.kicker": "Preparing Table",
    "game.loading.text": "Dealing the first hand...",
    "game.landscape.title": "Rotate your device",
    "game.landscape.sub": "Rocambor plays best in portrait mode",
    "game.closeHandTitle": "Five Consecutive Tricks",
    "game.closeHandHint": "Close hand now, or continue playing to imply Bola.",
    "game.auctionPanelTitle": "The Auction",
    "game.auctionPanelBeatOrPass": "Beat {bid} or pass",
    "game.auctionPanelChooseOpening": "Choose the opening call",
    "game.upgradePanelTitle": "Raise the call",
    "game.upgradePanelStatus": "You won with {bid}. Continue or raise.",
    "game.keepBid": "Keep {bid}",
    "game.keepBidDescChooseTrump": "Pick trump next",
    "game.keepBidDescProceed": "Proceed as-is",
    "game.auction.openCall": "Open the call",
    "game.auction.callOros": "Call Oros",
    "game.auction.flipTalon": "Flip the talon",
    "game.auction.playAlone": "Play alone",
    "game.auction.aloneOros": "Alone in Oros",
    "game.auction.allPassSpecial": "All-pass special",
    "game.auction.yieldCall": "Yield the call",
    "game.penetroPrompt": "No winning call. Let the resting seat join?",
    "game.penetro.decline": "Decline",
    "game.penetro.redeal": "Redeal hand",
    "game.penetro.play": "Play Penetro",
    "game.penetro.enter": "Resting player enters",
    "game.trumpPrompt": "Name the suit for this hand.",
    "game.exchange.chooseUpTo": "Choose up to {count} cards",
    "game.exchange.volteoDiscardPrompt": "Choose at least 1 card. First discard returns you to 9; extras exchange from talon.",
    "game.exchange.volteoTitle": "Volteo discard",
    "game.exchange.volteoConfirm": "Discard / Exchange",
    "game.exchange.selectedSingle": "{selected}/1 selected",
    "game.exchange.selectedMany": "{selected}/{max} selected",
    "game.exchange.trade": "Trade",
    "game.exchange.defer": "Defer",
    "game.volteoRevealTitle": "Volteo card",
    "game.volteoRevealHint": "The ombre takes this card, must discard once, then may exchange more.",
    "game.resultBanner.youWin": "You win",
    "game.resultBanner.otherWins": "{name} wins",
    "game.trickReason.trump": "trump advantage",
    "game.trickReason.highestSuit": "highest {suit}",
    "game.trickReason.highestCard": "highest card",
    "game.choosePenetro": "Choose whether to play Penetro",
    "game.chooseTrumpToast": "Choose trump suit",
    "postHand.subtitle": "Scores are settled and the next deal will begin automatically.",
    "postHand.outcome": "Outcome",
    "postHand.contract": "Contract",
    "postHand.trump": "Trump",
    "postHand.jugador": "Jugador",
    "postHand.resting": "Resting",
    "postHand.tricksWon": "Tricks Won",
    "postHand.matchScores": "Match Scores",
    "postHand.nextHand": "Next hand starting shortly",
    "match.kickerWin": "Rocambor Crown",
    "match.kickerLoss": "Final Table",
    "match.victory": "Victory!",
    "match.complete": "Match Complete",
    "match.winner": "Winner",
    "match.finalScore": "Final Score",
    "match.finalStandings": "Final Standings",
    "match.matchStats": "Match Stats",
    "match.nextTable": "Next Table",
    "match.target": "Target",
    "match.handsPlayed": "Hands Played",
    "match.mode": "Mode",
    "match.yourFinish": "Your Finish",
    "match.leaveRoom": "Leave Room",
    "match.rematchLocked": "Your rematch vote is locked in. The room will restart once the table agrees.",
    "match.rematchProgress": "{count} of {required} players are ready for another round.",
    "match.rematchIdle": "Stay with this table for an instant rematch or leave the room.",
    "match.playAgainWaiting": "Voted - waiting...",
    "match.winnerBadge": "Winner",
    "leaderboard.refreshFailed": "Could not refresh leaderboard",
    "leaderboard.back": "Back",
    "leaderboard.refreshing": "Refreshing...",
    "leaderboard.season": "Season {n}",
    "leaderboard.title": "The Grand Ledger",
    "leaderboard.subtitle": "A definitive record of the most esteemed Grandees across the Spanish realms.",
    "leaderboard.filterAll": "All players",
    "leaderboard.filterActive": "Active 30d",
    "leaderboard.filtersAria": "Leaderboard filters",
    "leaderboard.inlineErrorTitle": "Refresh failed.",
    "leaderboard.rankingsBeyond": "Rankings beyond the podium",
    "leaderboard.syncing": "Syncing the latest standings...",
    "leaderboard.findPlayer": "Find a player...",
    "leaderboard.rank": "Rank",
    "leaderboard.grandee": "Grandee",
    "leaderboard.activity": "Activity",
    "leaderboard.points": "Points",
    "leaderboard.winRate": "Win rate",
    "leaderboard.preparingLedger": "Preparing the season ledger",
    "leaderboard.loadingStandings": "Loading standings...",
    "leaderboard.unavailable": "Ledger unavailable",
    "leaderboard.couldNotOpen": "The rankings could not be opened.",
    "leaderboard.tryAgain": "Try again",
    "leaderboard.returnHome": "Return home",
    "leaderboard.noStandings": "No standings yet",
    "leaderboard.waitingFirst": "The ledger is waiting for its first name.",
    "leaderboard.finishMatch": "Finish a match to seed the season table and reveal the first podium.",
    "leaderboard.playFirstGame": "Play your first game",
    "leaderboard.noMatches": "No matches",
    "leaderboard.noPlayersView": "No players fit this ledger view.",
    "leaderboard.tryAnotherName": "Try another name or clear the activity filter to reopen the full standings.",
    "leaderboard.clearFilters": "Clear filters",
    "leaderboard.filteredPodium": "Filtered podium",
    "leaderboard.seasonLedger": "Season ledger",
    "leaderboard.onlyPodiumView": "Only podium finishes match this view",
    "leaderboard.onlyPodiumPopulated": "Only the podium is populated",
    "leaderboard.playersMatch": "{count} players match this view.",
    "leaderboard.updatedToday": "Updated {time}",
    "leaderboard.updatedAt": "Updated {time}",
    "leaderboard.awaitingSync": "Awaiting ledger sync",
    "leaderboard.rankTag": "Rank {rank}",
    "leaderboard.winsGames": "{wins} wins in {games} games",
    "leaderboard.noRecord": "No record",
    "leaderboard.noCompleted": "No completed table logged",
    "leaderboard.unknown": "Unknown",
    "leaderboard.lastUnavailable": "Last match unavailable",
    "leaderboard.today": "Today",
    "leaderboard.loggedToday": "Last table logged today",
    "leaderboard.thisWeek": "This week",
    "leaderboard.lastTableDaysAgo": "Last table {days}d ago",
    "leaderboard.thisMonth": "This month",
    "leaderboard.quiet": "Quiet",
    "leaderboard.showingMatching": "Showing {count} matching players",
    "leaderboard.showingRanked": "Showing {count} ranked players",
    "leaderboard.onlyPodiumCurrent": "Your current view only returns podium finishes.",
    "leaderboard.moreRankings": "More rankings will appear here as the season fills out.",
    "settings.title": "Settings",
    "settings.tablePreferences": "Table Preferences",
    "settings.tablePreferencesCopy": "Tune the salon for this device. Keep motion calm, audio intentional, and pick the deck that reads best at a glance.",
    "settings.gameplayFeel": "Gameplay Feel",
    "settings.behaviorTitle": "How the table behaves",
    "settings.enableSound": "Enable sound",
    "settings.enableSoundCaption": "Card flicks, trick wins, and table feedback.",
    "settings.reduceMotion": "Reduce motion",
    "settings.reduceMotionCaption": "Softens movement for a steadier reading pace.",
    "settings.espadaObligatoria": "Espada obligatoria",
    "settings.espadaCaption": "Preserves the traditional forced-espada rule set.",
    "settings.cards": "Cards",
    "settings.chooseDeck": "Choose your deck skin",
    "settings.selectedDeck": "Selected Deck",
    "settings.applied": "Settings applied",
    "profile.title": "Profile",
    "profile.memberSinceFallback": "Member since the first salon",
    "profile.memberSince": "Member since {date}",
    "profile.justNow": "Just now",
    "profile.hoursAgo": "{count} {unit} ago",
    "profile.daysAgo": "{count} {unit} ago",
    "profile.hour": "hour",
    "profile.hours": "hours",
    "profile.day": "day",
    "profile.days": "days",
    "profile.noRecentMatches": "Your recent salon matches will appear here.",
    "profile.finalScore": "Final score",
    "profile.winRate": "Win Rate",
    "profile.games": "Games",
    "profile.elo": "Elo",
    "profile.ledger": "Ledger",
    "profile.unranked": "Unranked",
    "profile.progressTo": "Progress to {tier}",
    "profile.crownReached": "Crown tier reached",
    "profile.lastMatch": "Last Match",
    "profile.waitingFirstGame": "Waiting for a first game",
    "profile.wins": "Wins",
    "profile.salonStanding": "Salon Standing",
    "profile.accountContext": "Current account context",
    "profile.medalGallery": "Medal Gallery",
    "profile.medalGalleryCaption": "Aristocratic achievements and milestones",
    "profile.recentMatches": "Recent Matches",
    "profile.recentMatchesCaption": "Your latest salon results on this browser",
    "profile.editProfile": "Edit Profile",
    "profile.editProfileCaption": "Update your display name and portrait",
    "profile.displayName": "Display Name",
    "profile.useNameSeed": "Use my name as avatar seed",
    "profile.uploadPortrait": "Upload Portrait",
    "profile.signInToUploadPortrait": "Sign in to upload a custom portrait.",
    "profile.uploadingPortrait": "Uploading portrait...",
    "profile.portraitUploaded": "Portrait updated.",
    "profile.portraitUploadFailed": "Unable to upload your portrait right now.",
    "profile.portraitFileType": "Choose an image file for your portrait.",
    "profile.portraitFileSize": "Portraits must be 5 MB or smaller.",
    "profile.randomize": "Randomize choices",
    "profile.avatarChoices": "Avatar choices",
    "profile.avatarOption": "Avatar {index}",
    "profile.selectedAvatar": "Selected avatar",
    "profile.locked": "Locked",
    "profile.forceTitle": "Choose Name & Avatar",
    "error.generic": "Something went wrong",
  },
  es: {
    "common.english": "English",
    "common.spanish": "Español",
    "common.language": "Idioma",
    "common.back": "Volver",
    "common.leave": "Salir",
    "common.cancel": "Cancelar",
    "common.create": "Crear",
    "common.join": "Unirse",
    "common.save": "Guardar",
    "common.copy": "Copiar",
    "common.copyCode": "Copiar código",
    "common.refresh": "Actualizar",
    "common.settings": "Ajustes",
    "common.profile": "Perfil",
    "common.you": "Tú",
    "common.host": "Anfitrión",
    "common.bot": "Bot",
    "common.open": "Libre",
    "common.ready": "Listo",
    "common.offline": "Desconectado",
    "common.mode": "Modo",
    "common.target": "Meta",
    "common.deck": "Baraja",
    "common.room": "Sala",
    "common.winner": "Ganador",
    "common.update": "Actualización",
    "common.loading": "Cargando",
    "common.startGame": "Empezar partida",
    "common.playAgain": "Jugar otra vez",
    "common.closeHand": "Cerrar mano",
    "common.closeModal": "Cerrar ventana",
    "player.unknown": "Jugador",
    "home.heroTag": "El salón espera",
    "home.welcome": "Bienvenido, {name}",
    "home.heroSubtitle": "Elige tu mesa y reclama tu gloria.",
    "home.selectVariation": "Elige la variante",
    "home.createRoom": "Crear sala",
    "home.createRoomSubtitle": "El salón del jugador",
    "home.quickPlay": "Partida rápida",
    "home.quickPlaySubtitle": "Partida instantánea",
    "home.joinByCode": "Unirse con código",
    "home.joinByCodeSubtitle": "Invitación privada",
    "home.queueSearching": "Buscando partida...",
    "home.queueSearchingPosition": "Buscando partida (posición {position})",
    "home.connectHint": "Conéctate al servidor para empezar una partida.",
    "home.connected": "Conectado al servidor del salón",
    "home.disconnected": "Desconectado del servidor del salón",
    "home.accountTitle": "Cuenta",
    "home.accountGuest": "Modo invitado",
    "home.signInGoogle": "Continuar con Google",
    "home.signOut": "Cerrar sesión",
    "home.signedOut": "Sesión cerrada",
    "home.authUnavailable": "El acceso con cuenta no está disponible ahora mismo.",
    "home.playStyle": "Tipo de partida",
    "home.playStyleFree": "Sin apuestas",
    "home.playStyleStakes": "Apuesta amistosa",
    "home.stakesHint": "Las mesas con apuesta amistosa requieren cuenta iniciada y usan fichas amistosas.",
    "home.walletBalance": "Saldo de fichas",
    "home.walletLoading": "Cargando saldo...",
    "home.walletRescue": "Pedir rescate",
    "home.walletRescueCooldown": "Rescate en espera",
    "home.walletRefreshFailed": "No se ha podido actualizar el saldo ahora mismo.",
    "home.walletRescueSuccess": "Has recibido las fichas de rescate.",
    "home.activityTitle": "Actividad reciente del salón",
    "home.activityLoading": "Cargando actividad...",
    "home.activityEmpty": "Los resultados recientes del salón aparecerán aquí.",
    "home.activityPot": "Bote {amount}",
    "home.quote": "\"El juego del Ombre es un juego de ingenio, fortuna y orgullo español.\"",
    "home.roomNameOptional": "Nombre de la sala (opcional)",
    "home.roomNamePlaceholder": "p. ej. La Mesa de Oro",
    "home.createRoomTitle": "Crear sala",
    "home.joinByCodeTitle": "Unirse con código",
    "home.roomCode": "Código de sala",
    "home.pointsToWin": "Puntos para ganar",
    "home.validRoomCode": "Introduce un código válido",
    "home.chooseNameAvatar": "Elige nombre y avatar",
    "app.connectionLost": "Conexión perdida: reconectando...",
    "lobby.sessionActive": "Sesión activa",
    "lobby.reconnecting": "Reconectando",
    "lobby.joiningSalon": "Entrando a tu salón",
    "lobby.roomPreparing": "La sala {code} se está preparando para el primer estado.",
    "lobby.preparing": "Preparando sala",
    "lobby.fetching": "Cargando asientos y estado de la sala",
    "lobby.waitSnapshot": "Te quedarás aquí un momento mientras llega el estado inicial de la mesa.",
    "lobby.privateRoomCode": "Código privado de la sala",
    "lobby.hostControls": "Controles del anfitrión",
    "lobby.matchStatus": "Estado de la partida",
    "lobby.awaitingPlayers": "Esperando jugadores",
    "lobby.waitingForHost": "Esperando a que el anfitrión empiece",
    "lobby.hostSeat": "Asiento del anfitrión",
    "lobby.yourSeat": "Tu asiento",
    "lobby.sitHere": "Sentarse aquí",
    "lobby.matchConfiguration": "Configuración de la partida",
    "lobby.invitation": "Invitación",
    "lobby.seats": "Asientos",
    "lobby.format": "Formato",
    "lobby.seatedReady": "Estás sentado y listo para la primera mano.",
    "lobby.botSeatMeta": "Esta silla la jugará un bot.",
    "lobby.reconnectSeatMeta": "Se perdió la conexión. El asiento se mantiene para reconexión.",
    "lobby.claimedReady": "Ocupado y listo para el salón.",
    "lobby.openSeatMeta": "Invita a un amigo o empieza con un bot.",
    "lobby.openSeat": "Asiento libre {seat}",
    "lobby.seat": "Asiento {seat}",
    "lobby.joinedToast": "{name} se unió",
    "lobby.codeCopied": "¡Código copiado!",
    "lobby.copyFailed": "No se pudo copiar",
    "lobby.allActiveSeatsClaimed": "Todos los asientos activos están ocupados y listos para la mano inicial.",
    "lobby.startNowBots": "Empieza ahora y {count} {seatWord} se llenarán con bots.",
    "lobby.emptySeatsBots": "{count} {seatWord} se llenarán con bots",
    "lobby.shareCode": "Comparte el código {code} para traer amigos directamente a este salón.",
    "lobby.activeSeatsClaimed": "Todos los asientos activos están ocupados y listos para el primer reparto.",
    "lobby.activeSeatsOpen": "{count} {seatWord} libres y podrán llenarse con bots al empezar la partida.",
    "lobby.withDeck": "{mode} a {target} puntos con la baraja española de 40 cartas.",
    "game.waiting": "Esperando...",
    "game.roundShort": "R: {hand}/{target}",
    "game.roundShortNoTarget": "R: {hand}",
    "game.round": "Ronda {hand}/{target}",
    "game.phaseAuction": "Fase: Subasta",
    "game.auction": "Subasta",
    "game.target": "Meta: {target}",
    "game.trump": "Triunfo: {suit}",
    "game.trumpUndecided": "Triunfo: por definir",
    "game.turn": "Turno: {name}{suffix}",
    "game.playTrick": "Jugar baza",
    "game.phaseChooseTrump": "Fase: Elegir triunfo",
    "game.chooseTrump": "Elegir triunfo",
    "game.phaseExchange": "Fase: Cambio",
    "game.exchange": "Cambio",
    "game.exchanging": "Cambio: {name}",
    "game.talonShort": "Talón: {count}",
    "game.talonRemaining": "Talón: quedan {count}",
    "game.penetro": "Penetro",
    "game.phasePenetro": "Fase: decisión de Penetro",
    "game.deciding": "{name} decide...",
    "game.dealing": "Repartiendo...",
    "game.handComplete": "Mano terminada",
    "game.matchComplete": "Partida terminada",
    "game.ping": "Latencia {value}",
    "game.profileMeta": "Sala {code}",
    "game.profileMetaFallback": "Jugador de mesa",
    "game.backHome": "Volver",
    "game.backHomeAria": "Volver al inicio",
    "game.settingsAria": "Ajustes",
    "game.soundAria": "Activar o desactivar sonido",
    "game.youUpper": "TÚ",
    "game.winner": "GANADOR",
    "game.yourHand": "Tu mano",
    "game.yourHandSwipe": "Tu mano (desliza)",
    "game.yourHandArea": "Área de tu mano",
    "game.swipeHintMobile": "Desliza",
    "game.tapToConfirm": "Toca de nuevo para jugar",
    "game.yourHandAria": "Tu mano",
    "game.swipe": "Desliza",
    "game.selectCard": "Selecciona una carta",
    "game.playCard": "Jugar carta",
    "game.selectCards": "Selecciona cartas",
    "game.keepAll": "Quedarse con todas",
    "game.yourTurn": "TU TURNO",
    "game.tricksWon": "Bazas ganadas: {count}",
    "game.tricksShort": "{count} bazas",
    "game.ombreRole": "Ombre",
    "game.opponents": "Oponentes",
    "game.passed": "Pasó",
    "game.resting": "Descansa",
    "game.role.you": "Tú",
    "game.role.ombre": "Jugador",
    "game.role.firstOpponent": "Primer contra",
    "game.role.secondOpponent": "Segundo contra",
    "game.role.seat": "Asiento {n}",
    "game.announce.pass": "{actor} pasa",
    "game.announce.bid": "{actor} canta {bid}",
    "game.announce.auctionWin": "{actor} gana la subasta con {bid}",
    "game.announce.winWith": "{actor} gana con {bid}",
    "game.announce.cardPlayed": "{actor} jugó {card}",
    "game.announce.point": "punto",
    "game.announce.points": "puntos",
    "game.announce.roundResultSelf": "Resultado de la ronda: +{points} {pointWord}",
    "game.announce.roundResultOthers": "Resultado de la ronda: {names} +{points}",
    "game.announce.roundComplete": "Ronda terminada",
    "game.announce.trumpSet": "Triunfo fijado en {suit}",
    "game.announce.volteoReveal": "Volteo descubre {card}",
    "game.invalid.exactOne": "Selecciona exactamente 1 carta para cambiar.",
    "game.invalid.validExchange": "Selecciona un cambio válido.",
    "game.invalid.waitExchange": "Espera tu turno de cambio.",
    "game.invalid.waitTurn": "Espera tu turno.",
    "game.invalid.followSuit": "Carta ilegal: debes asistir al palo si puedes.",
    "game.phaseBanner.auctionLeadNone": "Aún no hay canto líder",
    "game.phaseBanner.yourTurnChooseBid": "Tu turno: elige un canto",
    "game.phaseBanner.leadingBid": "Canto líder: {bid} de {name}",
    "game.phaseBanner.yourTurnChooseBidWithLeader": "Tu turno: elige un canto · {leader}",
    "game.phaseBanner.yourTurnChooseTrump": "Tu turno: elige triunfo",
    "game.phaseBanner.waitChooseTrump": "Esperando a {name} para elegir triunfo",
    "game.phaseBanner.waitTrump": "Esperando elección de triunfo",
    "game.phaseBanner.selectExchange": "Selecciona cartas para cambiar",
    "game.phaseBanner.volteoDiscard": "Elige al menos 1 carta: el primer descarte te devuelve a 9",
    "game.phaseBanner.waitExchange": "Esperando a {name} para cambiar",
    "game.phaseBanner.preparingExchange": "Preparando cambio",
    "game.phaseBanner.penetroDecision": "El jugador que descansa decide Penetro",
    "game.phaseBanner.waitLead": "Esperando salida",
    "game.phaseBanner.yourTurnPlay": "Tu turno: juega una carta legal",
    "game.phaseBanner.waitPlay": "Esperando a {name} para jugar",
    "game.phaseBanner.contractUpgradeOwn": "¡Ganaste la subasta! ¿Mejoras el contrato?",
    "game.phaseBanner.contractUpgradeWait": "El declarante estudia una mejora…",
    "game.phaseBanner.dealing": "Repartiendo cartas",
    "game.phaseBanner.handComplete": "Mano terminada",
    "game.phaseBanner.matchComplete": "Partida terminada",
    "game.kicker.trickResult": "Resultado de la baza",
    "game.kicker.yourTurn": "Tu turno",
    "game.kicker.update": "Actualización",
    "game.loading.kicker": "Preparando Mesa",
    "game.loading.text": "Repartiendo la primera mano...",
    "game.landscape.title": "Gira tu dispositivo",
    "game.landscape.sub": "Rocambor se juega mejor en modo vertical",
    "game.closeHandTitle": "Cinco bazas consecutivas",
    "game.closeHandHint": "Cierra la mano ahora o sigue jugando para implicar Bola.",
    "game.auctionPanelTitle": "La subasta",
    "game.auctionPanelBeatOrPass": "Supera {bid} o pasa",
    "game.auctionPanelChooseOpening": "Elige el canto de apertura",
    "game.upgradePanelTitle": "Subir el canto",
    "game.upgradePanelStatus": "Has ganado con {bid}. Continúa o sube.",
    "game.keepBid": "Mantener {bid}",
    "game.keepBidDescChooseTrump": "Elige triunfo después",
    "game.keepBidDescProceed": "Continúa igual",
    "game.auction.openCall": "Abre el canto",
    "game.auction.callOros": "Canta Oros",
    "game.auction.flipTalon": "Voltea el talón",
    "game.auction.playAlone": "Juega solo",
    "game.auction.aloneOros": "Solo en Oros",
    "game.auction.allPassSpecial": "Especial tras todos pasar",
    "game.auction.yieldCall": "Cede el canto",
    "game.penetroPrompt": "No hubo canto ganador. ¿Dejar entrar al que descansa?",
    "game.penetro.decline": "Rechazar",
    "game.penetro.redeal": "Repartir de nuevo",
    "game.penetro.play": "Jugar Penetro",
    "game.penetro.enter": "Entra el jugador que descansaba",
    "game.trumpPrompt": "Nombra el palo para esta mano.",
    "game.exchange.chooseUpTo": "Elige hasta {count} cartas",
    "game.exchange.volteoDiscardPrompt": "Elige al menos 1 carta. El primer descarte te devuelve a 9; las demás se cambian del talón.",
    "game.exchange.volteoTitle": "Descarte de Volteo",
    "game.exchange.volteoConfirm": "Descartar / Cambiar",
    "game.exchange.selectedSingle": "{selected}/1 seleccionada",
    "game.exchange.selectedMany": "{selected}/{max} seleccionadas",
    "game.exchange.trade": "Cambiar",
    "game.exchange.defer": "Ceder turno",
    "game.volteoRevealTitle": "Carta de volteo",
    "game.volteoRevealHint": "El jugador toma esta carta, debe descartar una y puede cambiar más.",
    "game.resultBanner.youWin": "Tú ganas",
    "game.resultBanner.otherWins": "{name} gana",
    "game.trickReason.trump": "ventaja de triunfo",
    "game.trickReason.highestSuit": "la más alta de {suit}",
    "game.trickReason.highestCard": "la carta más alta",
    "game.choosePenetro": "Elige si jugar Penetro",
    "game.chooseTrumpToast": "Elige el triunfo",
    "postHand.subtitle": "Las puntuaciones se han asentado y el siguiente reparto empezará automáticamente.",
    "postHand.outcome": "Resultado",
    "postHand.contract": "Contrato",
    "postHand.trump": "Triunfo",
    "postHand.jugador": "Jugador",
    "postHand.resting": "Descansa",
    "postHand.tricksWon": "Bazas ganadas",
    "postHand.matchScores": "Marcador",
    "postHand.nextHand": "La siguiente mano empieza en breve",
    "match.kickerWin": "Corona de Rocambor",
    "match.kickerLoss": "Mesa final",
    "match.victory": "¡Victoria!",
    "match.complete": "Partida terminada",
    "match.winner": "Ganador",
    "match.finalScore": "Puntuación final",
    "match.finalStandings": "Clasificación final",
    "match.matchStats": "Estadísticas",
    "match.nextTable": "Siguiente mesa",
    "match.target": "Meta",
    "match.handsPlayed": "Manos jugadas",
    "match.mode": "Modo",
    "match.yourFinish": "Tu puesto",
    "match.leaveRoom": "Salir de la sala",
    "match.rematchLocked": "Tu voto para la revancha ya quedó registrado. La sala se reiniciará cuando la mesa esté de acuerdo.",
    "match.rematchProgress": "{count} de {required} jugadores están listos para otra ronda.",
    "match.rematchIdle": "Quédate en esta mesa para una revancha instantánea o sal de la sala.",
    "match.playAgainWaiting": "Votado · esperando...",
    "match.winnerBadge": "Ganador",
    "leaderboard.refreshFailed": "No se pudo actualizar la clasificación",
    "leaderboard.back": "Volver",
    "leaderboard.refreshing": "Actualizando...",
    "leaderboard.season": "Temporada {n}",
    "leaderboard.title": "El Gran Libro",
    "leaderboard.subtitle": "Un registro definitivo de los grandes más ilustres a través de los reinos españoles.",
    "leaderboard.filterAll": "Todos",
    "leaderboard.filterActive": "Activos 30d",
    "leaderboard.filtersAria": "Filtros de clasificación",
    "leaderboard.inlineErrorTitle": "Falló la actualización.",
    "leaderboard.rankingsBeyond": "Puestos más allá del podio",
    "leaderboard.syncing": "Sincronizando la clasificación más reciente...",
    "leaderboard.findPlayer": "Buscar jugador...",
    "leaderboard.rank": "Puesto",
    "leaderboard.grandee": "Grande",
    "leaderboard.activity": "Actividad",
    "leaderboard.points": "Puntos",
    "leaderboard.winRate": "Tasa de victorias",
    "leaderboard.preparingLedger": "Preparando la tabla de la temporada",
    "leaderboard.loadingStandings": "Cargando clasificación...",
    "leaderboard.unavailable": "Clasificación no disponible",
    "leaderboard.couldNotOpen": "No se pudo abrir la clasificación.",
    "leaderboard.tryAgain": "Intentar de nuevo",
    "leaderboard.returnHome": "Volver al inicio",
    "leaderboard.noStandings": "Aún no hay clasificación",
    "leaderboard.waitingFirst": "El libro espera su primer nombre.",
    "leaderboard.finishMatch": "Termina una partida para sembrar la tabla de la temporada y revelar el primer podio.",
    "leaderboard.playFirstGame": "Juega tu primera partida",
    "leaderboard.noMatches": "Sin resultados",
    "leaderboard.noPlayersView": "Ningún jugador coincide con esta vista.",
    "leaderboard.tryAnotherName": "Prueba otro nombre o limpia el filtro de actividad para reabrir la clasificación completa.",
    "leaderboard.clearFilters": "Limpiar filtros",
    "leaderboard.filteredPodium": "Podio filtrado",
    "leaderboard.seasonLedger": "Libro de temporada",
    "leaderboard.onlyPodiumView": "Solo resultados de podio coinciden con esta vista",
    "leaderboard.onlyPodiumPopulated": "Solo el podio está poblado",
    "leaderboard.playersMatch": "{count} jugadores coinciden con esta vista.",
    "leaderboard.updatedToday": "Actualizado {time}",
    "leaderboard.updatedAt": "Actualizado {time}",
    "leaderboard.awaitingSync": "Esperando sincronización",
    "leaderboard.rankTag": "Puesto {rank}",
    "leaderboard.winsGames": "{wins} victorias en {games} partidas",
    "leaderboard.noRecord": "Sin registro",
    "leaderboard.noCompleted": "No hay mesas completadas registradas",
    "leaderboard.unknown": "Desconocido",
    "leaderboard.lastUnavailable": "Última partida no disponible",
    "leaderboard.today": "Hoy",
    "leaderboard.loggedToday": "Última mesa registrada hoy",
    "leaderboard.thisWeek": "Esta semana",
    "leaderboard.lastTableDaysAgo": "Última mesa hace {days}d",
    "leaderboard.thisMonth": "Este mes",
    "leaderboard.quiet": "Inactivo",
    "leaderboard.showingMatching": "Mostrando {count} jugadores coincidentes",
    "leaderboard.showingRanked": "Mostrando {count} jugadores clasificados",
    "leaderboard.onlyPodiumCurrent": "Tu vista actual solo devuelve resultados de podio.",
    "leaderboard.moreRankings": "Más puestos aparecerán aquí a medida que avance la temporada.",
    "settings.title": "Ajustes",
    "settings.tablePreferences": "Preferencias de mesa",
    "settings.tablePreferencesCopy": "Ajusta el salón para este dispositivo. Mantén el movimiento más calmado, el audio intencional y elige la baraja que se lea mejor a simple vista.",
    "settings.gameplayFeel": "Sensación de juego",
    "settings.behaviorTitle": "Cómo se comporta la mesa",
    "settings.enableSound": "Activar sonido",
    "settings.enableSoundCaption": "Flicks de cartas, bazas ganadas y respuesta de la mesa.",
    "settings.reduceMotion": "Reducir movimiento",
    "settings.reduceMotionCaption": "Suaviza el movimiento para una lectura más estable.",
    "settings.espadaObligatoria": "Espada obligatoria",
    "settings.espadaCaption": "Mantiene la regla tradicional de espada forzada.",
    "settings.cards": "Cartas",
    "settings.chooseDeck": "Elige tu diseño de baraja",
    "settings.selectedDeck": "Baraja seleccionada",
    "settings.applied": "Ajustes guardados",
    "profile.title": "Perfil",
    "profile.memberSinceFallback": "Miembro desde el primer salón",
    "profile.memberSince": "Miembro desde {date}",
    "profile.justNow": "Justo ahora",
    "profile.hoursAgo": "Hace {count} {unit}",
    "profile.daysAgo": "Hace {count} {unit}",
    "profile.hour": "hora",
    "profile.hours": "horas",
    "profile.day": "día",
    "profile.days": "días",
    "profile.noRecentMatches": "Tus partidas recientes del salón aparecerán aquí.",
    "profile.finalScore": "Puntuación final",
    "profile.winRate": "Tasa de victorias",
    "profile.games": "Partidas",
    "profile.elo": "Elo",
    "profile.ledger": "Tabla",
    "profile.unranked": "Sin puesto",
    "profile.progressTo": "Progreso hacia {tier}",
    "profile.crownReached": "Rango de corona alcanzado",
    "profile.lastMatch": "Última partida",
    "profile.waitingFirstGame": "Esperando la primera partida",
    "profile.wins": "Victorias",
    "profile.salonStanding": "Posición en el salón",
    "profile.accountContext": "Contexto actual de la cuenta",
    "profile.medalGallery": "Galería de medallas",
    "profile.medalGalleryCaption": "Logros e hitos aristocráticos",
    "profile.recentMatches": "Partidas recientes",
    "profile.recentMatchesCaption": "Tus últimos resultados del salón en este navegador",
    "profile.editProfile": "Editar perfil",
    "profile.editProfileCaption": "Actualiza tu nombre visible y retrato",
    "profile.displayName": "Nombre visible",
    "profile.useNameSeed": "Usar mi nombre como semilla del avatar",
    "profile.uploadPortrait": "Subir retrato",
    "profile.signInToUploadPortrait": "Inicia sesión para subir un retrato personalizado.",
    "profile.uploadingPortrait": "Subiendo retrato...",
    "profile.portraitUploaded": "Retrato actualizado.",
    "profile.portraitUploadFailed": "No se ha podido subir el retrato ahora mismo.",
    "profile.portraitFileType": "Elige un archivo de imagen para el retrato.",
    "profile.portraitFileSize": "Los retratos deben pesar 5 MB o menos.",
    "profile.randomize": "Aleatorizar opciones",
    "profile.avatarChoices": "Opciones de avatar",
    "profile.avatarOption": "Avatar {index}",
    "profile.selectedAvatar": "Avatar seleccionado",
    "profile.locked": "Bloqueado",
    "profile.forceTitle": "Elige nombre y avatar",
    "error.generic": "Algo salió mal",
  },
};

function interpolate(message: string, params?: Params): string {
  if (!params) return message;
  return message.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = params[key];
    return value === undefined ? `{${key}}` : String(value);
  });
}

export function localeTag(locale: Locale): string {
  return locale === "es" ? "es-ES" : "en-US";
}

export interface Translator {
  locale: Locale;
  t(key: string, params?: Params): string;
}

const _translatorCache = new Map<Locale, Translator>();

export function createTranslator(locale: Locale): Translator {
  const cached = _translatorCache.get(locale);
  if (cached) return cached;
  const instance: Translator = {
    locale,
    t(key: string, params?: Params): string {
      const message = MESSAGES[locale][key] ?? MESSAGES.en[key] ?? key;
      return interpolate(message, params);
    },
  };
  _translatorCache.set(locale, instance);
  return instance;
}

export function modeLabel(mode: "tresillo" | "quadrille", locale: Locale, compact = false): string {
  if (mode === "tresillo") return compact ? "Tresillo (3P)" : "Tresillo";
  return compact ? "Quadrille (4P)" : locale === "es" ? "Quadrille" : "Quadrille";
}

export function seatWord(count: number, locale: Locale): string {
  if (locale === "es") {
    return count === 1 ? "asiento libre" : "asientos libres";
  }
  return count === 1 ? "open seat" : "open seats";
}

export function emptySeatWord(count: number, locale: Locale): string {
  if (locale === "es") {
    return count === 1 ? "asiento vacío" : "asientos vacíos";
  }
  return count === 1 ? "empty seat" : "empty seats";
}

export function suitLabel(suit: string, locale: Locale): string {
  return SUIT_LABELS[suit as Suit]?.[locale] ?? suit;
}

export function bidDisplayLabel(value: string, locale: Locale): string {
  return BID_LABELS[value as Bid]?.[locale] ?? value;
}

export function contractDisplayLabel(
  contract: string | null,
  trump: string | null,
  locale: Locale
): string {
  if (!contract) return "";
  const base = CONTRACT_LABELS[contract]?.[locale] ?? contract;
  if (trump && !["oros", "solo_oros", "contrabola", "bola"].includes(contract)) {
    return `${base} ${suitLabel(trump, locale)}`;
  }
  return base;
}

export function phaseDisplayLabel(phase: string, locale: Locale): string {
  return PHASE_LABELS[phase]?.[locale] ?? phase;
}

export function positionLabel(
  position: "self" | "left" | "across" | "right",
  locale: Locale
): string {
  return POSITION_LABELS[position][locale];
}

export function cardDisplayLabel(card: Card, locale: Locale): string {
  const rank = RANK_LABELS[card.r]?.[locale] ?? String(card.r);
  return locale === "es"
    ? `${rank} de ${suitLabel(card.s, locale)}`
    : `${rank} of ${suitLabel(card.s, locale)}`;
}

export function formatPlacement(place: number, locale: Locale): string {
  if (locale === "es") return `${place}.º`;
  const remainder = place % 10;
  const teen = place % 100;
  if (teen >= 11 && teen <= 13) return `${place}th`;
  if (remainder === 1) return `${place}st`;
  if (remainder === 2) return `${place}nd`;
  if (remainder === 3) return `${place}rd`;
  return `${place}th`;
}

export function formatMemberSince(value: string, locale: Locale): string {
  const { t } = createTranslator(locale);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("profile.memberSinceFallback");
  return t("profile.memberSince", {
    date: new Intl.DateTimeFormat(localeTag(locale), {
      month: "short",
      year: "numeric",
    }).format(date),
  });
}

export function formatRelativeTime(value: string, locale: Locale): string {
  const { t } = createTranslator(locale);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("profile.justNow");
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.max(0, Math.round(diffMs / (1000 * 60 * 60)));
  if (diffHours < 1) return t("profile.justNow");
  if (diffHours < 24) {
    return t("profile.hoursAgo", {
      count: diffHours,
      unit: diffHours === 1 ? t("profile.hour") : t("profile.hours"),
    });
  }
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) {
    return t("profile.daysAgo", {
      count: diffDays,
      unit: diffDays === 1 ? t("profile.day") : t("profile.days"),
    });
  }
  return new Intl.DateTimeFormat(localeTag(locale), {
    month: "short",
    day: "numeric",
  }).format(date);
}
